import React from 'react'
import { StyleSheet, TouchableOpacity, View } from 'react-native'
import { useTheme } from '@react-navigation/native'
import { Text } from '@components'
import { Theme } from '@theme'

interface OptionChipsProps<T extends string> {
  label: string
  options: readonly { value: T; label: string }[]
  value: T | ''
  /** Tapping the selected chip again clears the choice (`''`). */
  onChange: (value: T | '') => void
  testID?: string
}

/** Single-choice pills (wraps on several lines). */
export function OptionChips<T extends string>({
  label,
  options,
  value,
  onChange,
  testID,
}: OptionChipsProps<T>) {
  const theme = useTheme()
  const styles = getStyles(theme)
  return (
    <View style={styles.wrapper}>
      <Text type="bodySmallBold" color={theme.colors.secondary}>
        {label}
      </Text>
      <View style={styles.chips} testID={testID}>
        {options.map((option) => {
          const selected = option.value === value
          return (
            <TouchableOpacity
              key={option.value}
              accessibilityRole="button"
              accessibilityState={{ selected }}
              aria-selected={selected}
              testID={`${testID ?? 'chip'}-${option.value}`}
              onPress={() => onChange(selected ? '' : option.value)}
              style={[styles.chip, selected && styles.chipSelected]}
            >
              <Text
                type="bodySmallBold"
                color={selected ? theme.colors.white : theme.colors.secondary}
              >
                {option.label}
              </Text>
            </TouchableOpacity>
          )
        })}
      </View>
    </View>
  )
}

const getStyles = (theme: Theme) =>
  StyleSheet.create({
    wrapper: { gap: theme.spacing.sm },
    chips: { flexDirection: 'row', flexWrap: 'wrap', gap: theme.spacing.sm },
    chip: {
      paddingHorizontal: theme.spacing.lg,
      paddingVertical: theme.spacing.sm,
      borderRadius: theme.border.jumbo,
      borderWidth: 1,
      borderColor: theme.colors.grey,
      backgroundColor: theme.colors.white,
    },
    chipSelected: { backgroundColor: theme.colors.primary, borderColor: theme.colors.primary },
  })
